"use client";

import {
  BookOpenText,
  ClipboardList,
  FileQuestion,
  ImageIcon,
  Layers3,
  WandSparkles,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";

export type GuideNeedSelection = {
  filter?: string;
  search?: string;
};

type NeedCard = {
  title: string;
  description: string;
  icon: LucideIcon;
  selection: GuideNeedSelection;
};

type BrowseByNeedProps = {
  hasGuides: boolean;
  onSelectNeed: (selection: GuideNeedSelection) => void;
};

const NEED_CARDS: NeedCard[] = [
  {
    title: "Plan a lesson",
    description: "Find staging and sequencing guidance for a real coursebook page.",
    icon: ClipboardList,
    selection: { filter: "Lesson planning" },
  },
  {
    title: "Teach a skill",
    description: "Browse reading, listening, speaking, writing, and grammar resources.",
    icon: BookOpenText,
    selection: { search: "skill" },
  },
  {
    title: "Prepare a test",
    description: "Use classroom assessment guidance that fits Moroccan English classes.",
    icon: FileQuestion,
    selection: { filter: "Tests" },
  },
  {
    title: "Understand MENFPESRS staging",
    description: "Connect lesson stages, objectives, timing, and classroom transitions.",
    icon: Layers3,
    selection: { filter: "MENFPESRS" },
  },
  {
    title: "Use textbook screenshots",
    description: "Turn a textbook page into a structured plan without losing the page context.",
    icon: ImageIcon,
    selection: { search: "textbook screenshot" },
  },
  {
    title: "Save planning time with AI",
    description: "Learn where Darsi can speed up planning while keeping teacher control.",
    icon: WandSparkles,
    selection: { search: "AI" },
  },
];

export function BrowseByNeed({ hasGuides, onSelectNeed }: BrowseByNeedProps) {
  return (
    <section className="bg-white px-4 py-12 text-slate-950 sm:px-6 sm:py-14 lg:px-8" aria-labelledby="browse-need-heading">
      <div className="mx-auto max-w-[1200px]">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-black text-[#087D2F]">Browse by need</p>
            <h2 id="browse-need-heading" className="mt-2 text-3xl font-black leading-tight text-slate-950 sm:text-4xl">
              Start from the teaching problem
            </h2>
          </div>
          <p className="max-w-xl text-base font-medium leading-7 text-slate-600">
            The library is organized around classroom jobs, not generic blog categories.
          </p>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {NEED_CARDS.map((card) => (
            <NeedCardItem key={card.title} card={card} hasGuides={hasGuides} onSelectNeed={onSelectNeed} />
          ))}
        </div>
      </div>
    </section>
  );
}

function NeedCardItem({
  card,
  hasGuides,
  onSelectNeed,
}: {
  card: NeedCard;
  hasGuides: boolean;
  onSelectNeed: (selection: GuideNeedSelection) => void;
}) {
  const Icon = card.icon;
  const sharedClassName =
    "group h-full min-h-[190px] rounded-[24px] border border-slate-200 bg-[#FBFEFA] p-5 text-left shadow-[0_24px_70px_-52px_rgba(15,23,42,0.5)] ring-1 ring-slate-100 transition-[transform,border-color,background-color] duration-150 hover:-translate-y-0.5 hover:border-green-200 hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-green-700 sm:p-6";

  const content = (
    <>
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-50 text-[#08A62F] ring-1 ring-green-100">
        <Icon className="h-6 w-6" strokeWidth={2.5} aria-hidden />
      </div>
      <h3 className="mt-5 text-xl font-black leading-tight text-slate-950">{card.title}</h3>
      <p className="mt-3 text-[15px] font-medium leading-7 text-slate-600">{card.description}</p>
      <span
        className={cn(
          "mt-5 inline-flex rounded-full px-3 py-1 text-xs font-black",
          hasGuides ? "bg-green-100 text-[#087D2F]" : "bg-amber-100 text-amber-800",
        )}
      >
        {hasGuides ? "Filter guides" : "Coming soon"}
      </span>
    </>
  );

  if (!hasGuides) {
    return <article className={sharedClassName}>{content}</article>;
  }

  return (
    <button type="button" className={sharedClassName} onClick={() => onSelectNeed(card.selection)}>
      {content}
    </button>
  );
}
